import { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";

// Constants for ISO week calculations
const MILLISECONDS_PER_DAY = 86_400_000;
const SUNDAY = 7;
const THURSDAY_OFFSET = 4; // Days to Thursday from Monday

/**
 * Moves date to Thursday of the same ISO week
 * (ISO week belongs to the year of its Thursday)
 */
function _moveToThursdayOfWeek(date: Date): Date {
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayOfWeek = utcDate.getUTCDay() || SUNDAY; // Convert Sunday (0) to 7
  utcDate.setUTCDate(utcDate.getUTCDate() + THURSDAY_OFFSET - dayOfWeek);
  return utcDate;
}

/**
 * Gets ISO week number for given date (1-53)
 */
export function getISOWeek(date = new Date()): number {
  const thursday = _moveToThursdayOfWeek(date);
  const yearStart = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 1));
  const daysSinceYearStart =
    (thursday.getTime() - yearStart.getTime()) / MILLISECONDS_PER_DAY + 1;
  return Math.ceil(daysSinceYearStart / 7);
}

/**
 * Gets ISO week year for given date (can differ from calendar year)
 */
export function getISOWeekYear(date = new Date()): number {
  return _moveToThursdayOfWeek(date).getUTCFullYear();
}

/**
 *  Determines the current financial quarter based on the current date
 */
export function getCurrentFinancialQuarter(
  isGregorian: boolean,
  metaData: ForecastMetaDataType
) {
  const currentWeek = getISOWeek();
  const currentYear = getISOWeekYear();
  const key = `${currentYear}${String(currentWeek).padStart(2, "0")}`;
  const financialQuarter =
    metaData.weekFiscalQuarterMapping.fiscal_quarter_name[key];
  const gregorianCalendarQuarter =
    metaData.weekQuarterMapping.quarter_year_name[key];

  return isGregorian ? gregorianCalendarQuarter : financialQuarter;
}

/**
 * Determines the current half-year, derived from the current quarter with the
 * same fold the backend calendar uses (Q1/Q2 → H1, Q3/Q4 → H2)
 */
export function getCurrentFinancialHalfYear(
  isGregorian: boolean,
  metaData: ForecastMetaDataType
): string | undefined {
  const quarter = getCurrentFinancialQuarter(isGregorian, metaData);
  const quarterNumber = parseInt(quarter?.slice(1) ?? "", 10);

  if (isNaN(quarterNumber)) {
    return undefined;
  }
  return quarterNumber <= 2 ? "H1" : "H2";
}
